import { ReactNode, useEffect, useId, useState } from 'react';
import { CheckFilledIcon, ExclamationOutlineIcon, InfoIcon, RemoveFilledIcon } from '@icons';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { TOAST_DEFAULT_DURATION, getAnchorName } from '@shared/styles';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import classNames from './Toast.module.css';

export type ToastStyle = 'confirmation' | 'warning' | 'info' | 'important';

export interface ToastProps {
  style?: ToastStyle;
  position?: 'top' | 'bottom';
  /** 'short' (default) keeps actions inline with the message; 'long' stacks them on their own row below */
  layout?: 'short' | 'long';
  message?: string | ReactNode;
  icon?: ReactNode;
  className?: string;
  duration?: number;
  autoClose?: boolean;
  tapToClose?: boolean;
  prioritize?: boolean;
  onClose?: () => void;
  actions?: ToastActionProps[];
  showCloseButton?: boolean;
}

export function Toast({
  style = 'confirmation',
  icon = getToastIcon({ style }),
  layout = 'short',
  message,
  className,
  duration = TOAST_DEFAULT_DURATION,
  autoClose = true,
  onClose,
  actions = [],
  showCloseButton = actions.length > 0,
  tapToClose = actions.length === 0 || !showCloseButton,
}: ToastProps) {
  const [mountTime] = useState(Date.now());
  const isLongLayout = layout === 'long';
  const id = useId();
  const viewTransitionName = `toast-${getAnchorName(id)}`;

  // if autoClose is set, close the toast after the duration
  useEffect(() => {
    if (!autoClose) {
      return;
    }
    const { signal, abort } = getController();
    // if mounted for longer than the duration, close immediately
    const timeElapsed = Date.now() - mountTime;
    const delay = Math.max(duration - timeElapsed, 200);
    setControlledTimeout(() => onClose?.(), delay, signal);
    return abort;
  }, [autoClose, duration, mountTime, onClose]);

  return (
    <View className={classNames.toast} style={{ viewTransitionName }}>
      <View
        testID="toast-message"
        onClick={tapToClose ? onClose : undefined}
        className={[
          classNames.toastCard,
          icon ? classNames.withIcon : classNames.withoutIcon,
          classNames[style],
          tapToClose && classNames.tapToClose,
          isLongLayout && classNames.longLayout,
          className,
        ]}
      >
        <View className={classNames.content}>
          {icon && <View className={classNames.icon}>{icon}</View>}
          <View className={classNames.message}>{message}</View>
        </View>
        <ToastActions actions={actions} showCloseButton={showCloseButton} onClose={onClose} />
      </View>
    </View>
  );
}

function ToastActions({
  actions,
  showCloseButton,
  onClose,
}: Pick<ToastProps, 'actions' | 'showCloseButton' | 'onClose'>) {
  return (
    <View row className={classNames.actions}>
      {actions?.map((action, index) => (
        <ToastAction
          key={index}
          {...action}
          onPress={() => {
            action.onPress?.();
            onClose?.();
          }}
        />
      ))}
      {showCloseButton && <CloseToastButton onClose={onClose} />}
    </View>
  );
}

export interface ToastActionProps {
  onPress?: () => void;
  href?: string;
  label: string;
}

function ToastAction({ onPress, href, label }: ToastActionProps) {
  if (href) {
    return null;
  }
  return <Button variant="ghost" onClick={onPress} className={classNames.action} label={label} />;
}

function CloseToastButton({ onClose }: Pick<ToastProps, 'onClose'>) {
  return (
    <Button
      testID="toast-close-button"
      variant="ghost"
      icon="exit"
      iconOnly
      onClick={onClose}
      className={classNames.closeButton}
    />
  );
}

function getToastIcon({ style }: ToastProps) {
  switch (style) {
    case 'confirmation':
      return <CheckFilledIcon />;
    case 'warning':
      return <RemoveFilledIcon />;
    case 'info':
      return <InfoIcon />;
    case 'important':
      return <ExclamationOutlineIcon />;
    default:
      return null;
  }
}
